// Settings card — Referral auto-touch (AI-fired thank-you SMS/email
// after someone fills out the public referral form). Operator opts
// in, customizes the templates, and that's it. The intake endpoint
// reads these settings + dispatches on the next form submit.
//
// All four fields live in user_state.cfg_settings_v1 (no migration)
// so this ships clean.

import React from 'react';
import { Sparkles, MessageSquare, Mail, ChevronDown, ChevronRight } from 'lucide-react';

const TOKEN_HINT = 'Tokens: {first_name} {agent_name} {agency_name}';

const DEFAULT_SMS = `Hi {first_name}, this is {agent_name} with {agency_name}. Thanks for reaching out — I'll call you within 24 hours. Reply STOP to opt out.`;
const DEFAULT_EMAIL_SUBJECT = `Thanks for reaching out — {agency_name}`;
const DEFAULT_EMAIL_BODY    = `Hi {first_name},

Thanks for filling out our form. I'll reach out within 24 hours to learn more about your situation and answer any questions you have.

— {agent_name}
{agency_name}`;

// Per-niche defaults — must mirror api/public/referral-intake.js
// NICHE_DEFAULTS exactly. If you change one, change both. Operators
// who DON'T enter custom per-niche templates get these as the
// "compliance-aware" fallback when a niche-routed referral comes in.
const NICHE_DEFAULTS = {
  life: {
    label: 'Life / IUL',
    tone:  'amber',
    sms:       `Hi {first_name}, this is {agent_name} with {agency_name}. Thanks for checking out the IUL options — I'll call you within 24 hours to walk through what your numbers would actually look like. Reply STOP to opt out.`,
    emailSubj: `Your IUL questions — {agency_name}`,
    emailBody: `Hi {first_name},

Thanks for filling out the form on my referral page. I'll reach out within 24 hours to run your specific numbers through carrier illustration software and answer any questions you have.

No obligation — even if IUL isn't right for you, you'll walk away knowing whether it should be.

— {agent_name}
{agency_name}`,
  },
  health: {
    label: 'Health / ACA',
    tone:  'sky',
    sms:       `Hi {first_name}, this is {agent_name} with {agency_name}. Thanks for reaching out about Marketplace coverage — I'll call you within 24 hours to check your subsidy and walk through your zip code's plans. Reply STOP to opt out.`,
    emailSubj: `Your Marketplace options — {agency_name}`,
    emailBody: `Hi {first_name},

Thanks for reaching out about health insurance. I'll reach out within 24 hours so we can review your eligibility for premium tax credits and look at every plan available in your zip code.

Working with a licensed agent costs you the same as enrolling on HealthCare.gov directly — carriers pay the commission, your premium doesn't change.

— {agent_name}
{agency_name}`,
  },
  medicare: {
    label: 'Medicare',
    tone:  'emerald',
    sms:       `Hi {first_name}, this is {agent_name} with {agency_name}. Thanks for reaching out about Medicare — I'll call you within 24 hours. Before we discuss specific plans, CMS requires a quick Scope of Appointment form on file (60 seconds, I'll send a link). Reply STOP to opt out.`,
    emailSubj: `Your Medicare options — {agency_name}`,
    emailBody: `Hi {first_name},

Thanks for reaching out about Medicare. I'll reach out within 24 hours to walk through your Rx list, doctors, and the Advantage / Supplement options in your county.

Quick heads-up: before any specific plan discussion, CMS requires you to sign a Scope of Appointment (SOA) — takes about 60 seconds and just documents which plan types we have permission to discuss. I'll send a secure link.

— {agent_name}
{agency_name}`,
  },
};

export default function ReferralAutoTouchPanel({ settings = {}, setSettings = () => {}, showToast = () => {} }) {
  const enabled       = settings.referralAutoTouchEnabled === true;
  const smsBody       = settings.referralAutoTouchSms          ?? DEFAULT_SMS;
  const emailSubject  = settings.referralAutoTouchEmailSubject ?? DEFAULT_EMAIL_SUBJECT;
  const emailBody     = settings.referralAutoTouchEmailBody    ?? DEFAULT_EMAIL_BODY;

  const update = (patch) => setSettings({ ...settings, ...patch });

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-bold text-slate-900 inline-flex items-center gap-2">
            <Sparkles size={14} className="text-amber-600"/>
            Referral auto-touch
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            When a prospect fills out your public referral form, fire an automatic SMS (and email if they shared one) so they hear from "you" inside 60 seconds — before they ghost.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-xs cursor-pointer shrink-0">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => update({ referralAutoTouchEnabled: e.target.checked })}
          />
          <span className="font-semibold text-slate-700">
            {enabled ? 'On' : 'Off'}
          </span>
        </label>
      </div>

      {enabled && (
        <div className="space-y-3 pt-2 border-t border-slate-100">
          {/* SMS template */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 inline-flex items-center gap-1">
              <MessageSquare size={10}/> SMS body
            </label>
            <textarea
              value={smsBody}
              onChange={(e) => update({ referralAutoTouchSms: e.target.value })}
              rows={3}
              maxLength={320}
              className="w-full mt-1 text-sm border border-slate-300 rounded px-2 py-1.5 font-mono"
              placeholder={DEFAULT_SMS}
            />
            <div className="text-[10px] text-slate-400 mt-0.5 flex items-center justify-between gap-2">
              <span>{TOKEN_HINT}</span>
              <span className="tabular-nums">{smsBody.length} / 320</span>
            </div>
          </div>

          {/* Email template — optional, fires only when prospect shared email */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 inline-flex items-center gap-1">
              <Mail size={10}/> Email subject
            </label>
            <input
              type="text"
              value={emailSubject}
              onChange={(e) => update({ referralAutoTouchEmailSubject: e.target.value })}
              className="w-full mt-1 text-sm border border-slate-300 rounded px-2 py-1.5"
              placeholder={DEFAULT_EMAIL_SUBJECT}
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
              Email body
            </label>
            <textarea
              value={emailBody}
              onChange={(e) => update({ referralAutoTouchEmailBody: e.target.value })}
              rows={5}
              className="w-full mt-1 text-sm border border-slate-300 rounded px-2 py-1.5"
              placeholder={DEFAULT_EMAIL_BODY}
            />
            <div className="text-[10px] text-slate-400 mt-0.5">{TOKEN_HINT}</div>
          </div>

          {/* Per-niche template overrides. Each section is collapsed
              by default so the panel stays tight. Resolution order at
              send time: per-niche custom → generic custom → per-niche
              default → generic default. Leave a section's fields blank
              to use the compliance-aware niche default below. */}
          <div className="pt-2 border-t border-slate-100">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
              Per-niche overrides
            </div>
            <p className="text-[11px] text-slate-500 mb-2 leading-relaxed">
              When a referral comes in from your IUL / Health / Medicare landing section, the auto-touch matches the prospect's intent. Leave any field blank to use the compliance-aware niche default shown beneath each input.
            </p>
            <div className="space-y-2">
              {['life', 'health', 'medicare'].map(nicheKey => (
                <NicheTemplateEditor
                  key={nicheKey}
                  nicheKey={nicheKey}
                  settings={settings}
                  setSettings={setSettings}
                />
              ))}
            </div>
          </div>

          <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 leading-relaxed">
            <strong className="text-slate-700">How it works:</strong> SMS fires the moment the form submits (wallet-gated, A2P-gated). Email fires only when the prospect provides one. Both are logged to the Inbox under <code className="bg-white px-1 rounded text-[10px]">source=referral_auto_touch</code> so you see what went out.
          </div>
        </div>
      )}
    </div>
  );
}

// Per-niche template editor — collapsible, three fields (SMS / email
// subject / email body), each showing its niche-specific default
// beneath the input so the operator knows what they'd inherit. Keys
// in settings are camelCased (referralAutoTouchSmsLife, etc.) matching
// the resolution order in api/public/referral-intake.js.
function NicheTemplateEditor({ nicheKey, settings, setSettings }) {
  const [open, setOpen] = React.useState(false);
  const meta = NICHE_DEFAULTS[nicheKey];
  const Key  = nicheKey.charAt(0).toUpperCase() + nicheKey.slice(1);
  const smsK = `referralAutoTouchSms${Key}`;
  const subK = `referralAutoTouchEmailSubject${Key}`;
  const bodK = `referralAutoTouchEmailBody${Key}`;
  const sms  = settings[smsK] ?? '';
  const sub  = settings[subK] ?? '';
  const bod  = settings[bodK] ?? '';
  const isCustomized = !!(sms || sub || bod);
  const update = (patch) => setSettings({ ...settings, ...patch });

  const toneClass = meta.tone === 'amber'
    ? 'border-amber-200 bg-amber-50'
    : meta.tone === 'sky'
      ? 'border-sky-200 bg-sky-50'
      : 'border-emerald-200 bg-emerald-50';
  const chipClass = meta.tone === 'amber'
    ? 'bg-amber-200 text-amber-900'
    : meta.tone === 'sky'
      ? 'bg-sky-200 text-sky-900'
      : 'bg-emerald-200 text-emerald-900';

  return (
    <div className={`border rounded-lg ${open ? 'border-slate-300 bg-white' : `${toneClass}`}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full px-3 py-2 flex items-center gap-2 text-left"
      >
        {open ? <ChevronDown size={14} className="text-slate-500"/> : <ChevronRight size={14} className="text-slate-500"/>}
        <span className="text-sm font-bold text-slate-900">{meta.label}</span>
        {isCustomized && (
          <span className={`text-[10px] font-black uppercase tracking-wider rounded-full px-2 py-0.5 ${chipClass}`}>
            customized
          </span>
        )}
        {!isCustomized && (
          <span className="text-[10px] text-slate-500 italic">using compliance-aware default</span>
        )}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3 border-t border-slate-200">
          {/* SMS */}
          <div className="pt-2">
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 inline-flex items-center gap-1">
              <MessageSquare size={10}/> SMS body for {meta.label}
            </label>
            <textarea
              value={sms}
              onChange={(e) => update({ [smsK]: e.target.value })}
              rows={3}
              maxLength={320}
              className="w-full mt-1 text-sm border border-slate-300 rounded px-2 py-1.5 font-mono"
              placeholder={meta.sms}
            />
            <div className="text-[10px] text-slate-400 mt-0.5 flex items-center justify-between gap-2">
              <span>{TOKEN_HINT}</span>
              <span className="tabular-nums">{sms.length} / 320</span>
            </div>
            <details className="mt-1">
              <summary className="text-[10px] text-slate-500 cursor-pointer hover:text-slate-700">
                See the default SMS (used if blank)
              </summary>
              <pre className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded p-2 mt-1 whitespace-pre-wrap font-mono">{meta.sms}</pre>
            </details>
          </div>

          {/* Email subject */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 inline-flex items-center gap-1">
              <Mail size={10}/> Email subject for {meta.label}
            </label>
            <input
              type="text"
              value={sub}
              onChange={(e) => update({ [subK]: e.target.value })}
              className="w-full mt-1 text-sm border border-slate-300 rounded px-2 py-1.5"
              placeholder={meta.emailSubj}
            />
            <details className="mt-1">
              <summary className="text-[10px] text-slate-500 cursor-pointer hover:text-slate-700">
                See the default subject
              </summary>
              <pre className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded p-2 mt-1 whitespace-pre-wrap font-mono">{meta.emailSubj}</pre>
            </details>
          </div>

          {/* Email body */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
              Email body for {meta.label}
            </label>
            <textarea
              value={bod}
              onChange={(e) => update({ [bodK]: e.target.value })}
              rows={6}
              className="w-full mt-1 text-sm border border-slate-300 rounded px-2 py-1.5"
              placeholder={meta.emailBody}
            />
            <details className="mt-1">
              <summary className="text-[10px] text-slate-500 cursor-pointer hover:text-slate-700">
                See the default email body
              </summary>
              <pre className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded p-2 mt-1 whitespace-pre-wrap font-mono">{meta.emailBody}</pre>
            </details>
          </div>

          {isCustomized && (
            <button
              type="button"
              onClick={() => update({ [smsK]: '', [subK]: '', [bodK]: '' })}
              className="text-[11px] text-slate-500 hover:text-rose-700 underline"
            >
              Reset {meta.label} to defaults
            </button>
          )}
        </div>
      )}
    </div>
  );
}
